// src/components/Sidebar.js
import React, { useState, useMemo } from "react";
import { NavLink } from "react-router-dom";
import { renderIcon } from "./iconMap";
import { baseMenus } from "../config/menuBase";
import { useSendLogoutMutation } from "../features/auth/authApiSlice";
import { useGetMenusQuery } from "../features/menus/menusApiSlice";
import { BsList } from "react-icons/bs";
import useAuth from "../hooks/useAuth";

const sidebarTheme = {
  containerBg: "bg-white",
  containerText: "text-neutral-900",
  mutedText: "text-neutral-500",
  headerAccentBg: "bg-neutral-50",
  icon: "text-neutral-700",
  divider: "border-neutral-200",
  linkBase: "flex items-center gap-3 p-2 rounded-sm text-sm transition-colors",
  linkText: "text-neutral-900",
  linkHover: "hover:bg-neutral-100 hover:text-neutral-900",
  linkActive: "bg-neutral-100 text-neutral-900",
  signOut: "text-red-600 hover:text-red-700",
  version: "text-neutral-500 text-sm text-center",
};

const composeLink = (isActive = false) =>
  `${sidebarTheme.linkBase} ${isActive ? sidebarTheme.linkActive : `${sidebarTheme.linkText} ${sidebarTheme.linkHover}`}`;

const Sidebar = ({ isOpen, toggleSidebar }) => {
  const [collapsed, setCollapsed] = useState({});
  const {
    displayname,
    isGlobalAdmin,
    accessibleApps,
    hasAppRole,
    getAppAccess,
  } = useAuth();

  const [sendLogout] = useSendLogoutMutation();
  const { data: extraMenus = [] } = useGetMenusQuery();

  const toggleMenu = (key) => setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));

  const canSeeParentMenu = (menu) => {
    if (menu.public) return true;
    if (isGlobalAdmin) return true;
    if (menu.adminOnly) return false;
    if (!menu.app) return true;
    return accessibleApps.includes(menu.app);
  };

  const canSeeChildMenu = (parentApp, child) => {
    if (isGlobalAdmin) return true;
    if (!child.allowedRoles || child.allowedRoles.length === 0) return true;
    return child.allowedRoles.some((role) => hasAppRole(parentApp, role));
  };

  const filterMenus = (menus) => {
    return menus
      .filter(canSeeParentMenu)
      .map((menu) => {
        if (!Array.isArray(menu.children)) return menu;

        const filteredChildren = menu.children.filter((child) =>
          canSeeChildMenu(menu.app, child)
        );

        return {
          ...menu,
          children: filteredChildren,
        };
      })
      .filter((menu) => !Array.isArray(menu.children) || menu.children.length > 0);
  };

  const menuTop = useMemo(() => {
    const tops = [...(baseMenus.top || [])];
    extraMenus
      .filter((m) => m.enabled && (m.slot || "bottom") === "top")
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .forEach((m) => tops.push(m));
    return filterMenus(tops);
  }, [extraMenus, isGlobalAdmin, accessibleApps]);

  const menuBottom = useMemo(() => {
    const bots = [...(baseMenus.bottom || [])];
    extraMenus
      .filter((m) => m.enabled && (m.slot || "bottom") === "bottom")
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .forEach((m) => bots.push(m));
    return filterMenus(bots);
  }, [extraMenus, isGlobalAdmin, accessibleApps]);

  const primaryPajakAccess = getAppAccess("PAJAK_AUTOMATION");
  const headerCustomerId = primaryPajakAccess?.scope?.customer_id ?? "-";
  const headerTaxpayerId = primaryPajakAccess?.scope?.taxpayer_id ?? "-";

  const Section = ({ data }) => (
    <ul className="flex flex-col gap-1 mt-2">
      {data.map((menu) => {
        const hasChildren = Array.isArray(menu.children) && menu.children.length > 0;

        return (
          <li key={menu.key} className="cursor-pointer">
            <div
              className={`${sidebarTheme.linkBase} ${sidebarTheme.linkText} ${hasChildren ? "justify-between" : ""}`}
              onClick={() => hasChildren && toggleMenu(menu.key)}
              role={hasChildren ? "button" : undefined}
              aria-expanded={hasChildren ? !!collapsed[menu.key] : undefined}
            >
              <div className="flex items-center gap-3 truncate">
                <span className={sidebarTheme.icon}>{renderIcon(menu.icon, {})}</span>
                <span className="truncate">{menu.title}</span>
              </div>
              {hasChildren && (
                <span className={`${sidebarTheme.icon} transition-transform ${collapsed[menu.key] ? "rotate-90" : ""}`}>
                  ▸
                </span>
              )}
            </div>

            {hasChildren && collapsed[menu.key] && (
              <ul className="pl-4 mt-1">
                {menu.children.map((child, idx) => {
                  const isExternal = /^https?:\/\//i.test(child.path) || child.external;

                  const LinkContent = (
                    <>
                      <span className={sidebarTheme.icon}>{renderIcon(child.icon, {})}</span>
                      <span className="truncate">{child.label}</span>
                    </>
                  );

                  if (isExternal) {
                    return (
                      <li key={idx}>
                        <a
                          href={child.path}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={composeLink(false) + " w-full"}
                        >
                          {LinkContent}
                        </a>
                      </li>
                    );
                  }

                  return (
                    <li key={idx}>
                      <NavLink
                        to={child.path}
                        end
                        className={({ isActive }) => composeLink(isActive) + " w-full"}
                      >
                        {LinkContent}
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );

  return (
    <div
      className={`${sidebarTheme.containerBg} ${sidebarTheme.containerText} flex flex-col h-full p-2 fixed top-0 left-0 z-50 transition-width duration-200`}
      style={{ width: isOpen ? "16rem" : "3rem" }}
    >
      {isOpen ? (
        <div className="flex items-center justify-between px-3 py-3">
          <div className="flex items-center gap-3">
            <div className={`${sidebarTheme.headerAccentBg} w-8 h-8 rounded flex items-center justify-center`}>
              <span className="text-sm font-semibold">
                <img src="./img/800_tms_whfrm_whbg.png" alt="Logo" />
              </span>
            </div>
            <div className="truncate">
              <div className="font-bold text-lg">Mudah Panel</div>
              <div className={sidebarTheme.mutedText + " text-xs"}>
                {displayname}
                {isGlobalAdmin ? " (GLOBAL ADMIN)" : ` (${headerCustomerId}) (${headerTaxpayerId})`}
              </div>
            </div>
          </div>

          <button id="menu-button" onClick={toggleSidebar} className="p-2 rounded bg-neutral-800 text-white">
            <BsList fontSize={18} />
          </button>
        </div>
      ) : (
        <div className="flex flex-col h-full">
          <div className="w-full flex items-start justify-end px-1 py-2">
            <button id="menu-button" onClick={toggleSidebar} className="p-2 rounded bg-neutral-800 text-white">
              <BsList fontSize={18} />
            </button>
          </div>

          <div className="flex-1 flex items-center justify-center">
            <div
              id="vertical-label"
              className="text-center text-xs text-neutral-400"
              style={{ writingMode: "vertical-lr", textOrientation: "mixed", transform: "rotate(180deg)" }}
            >
              Menu
            </div>
          </div>
        </div>
      )}

      {isOpen && (
        <>
          <div className={`border-t ${sidebarTheme.divider} mt-2`} />
          <div className="overflow-y-auto mt-3 pb-4">
            <Section data={menuTop} />
            <div className={`border-t ${sidebarTheme.divider} my-3`} />
            <Section data={menuBottom} />
          </div>

          <div className="mt-auto px-3 pb-4">
            <div className="pt-4">
              <button onClick={() => sendLogout()} className={`w-full text-left ${sidebarTheme.signOut}`}>
                Sign Out
              </button>
            </div>
            <div id="version" className={`${sidebarTheme.version} mt-4`}>
              Version 3
              <br />
              Version 4 is coming
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Sidebar;